import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Spinner, Alert, Row, Col, Form, Button, ListGroup,ButtonGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from './api';
import './RandevuTakvimi.css'; // Takvim stillerini kullandığımızdan emin olalım
import './KlinikTakvim.css'; // FullCalendar tema stilleri (yeniden kullanim)

// Gün içindeki randevu saat aralıkları
const zamanAraliklari = Array.from({ length: 18 }, (_, i) => {
  const saat = 9 + Math.floor(i / 2);
  const dakika = i % 2 === 0 ? '00' : '30';
  return `${saat.toString().padStart(2, '0')}:${dakika}`;
});

// Tarihi API'ye uygun "YYYY-MM-DD" formatına çeviren fonksiyon
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// İki tarihi gün bazında karşılaştıran fonksiyon
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function RandevuTakvimi({ theme }) {
  // State Tanımlamaları
  const [danisanlar, setDanisanlar] = useState([]);
  const [diyetisyenProgrami, setDiyetisyenProgrami] = useState([]);
  const [randevuListesi, setRandevuListesi] = useState([]);
  const [siralama, setSiralama] = useState('asc'); // 'asc' (artan), 'desc' (azalan)

  const [seciliDanisan, setSeciliDanisan] = useState(null);
  const [seciliTarih, setSeciliTarih] = useState(new Date());
  const [seciliSaat, setSeciliSaat] = useState(null);
  const [konu, setKonu] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const formRef = useRef(null);

  // Veri Çekme Fonksiyonları
  const verileriGetir = useCallback(async () => {
    try {
      setLoading(true);
      const [danisanRes, randevuRes] = await Promise.all([
        api.get('/diyetisyen/danisanlar'),
        api.get('/diyetisyen/randevular')
      ]);
      setDanisanlar(danisanRes.data.map(d => ({ value: d.id, label: `${d.ad} ${d.soyad}` })));
      setRandevuListesi(randevuRes.data);
    } catch (err) {
      setError("Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verileriGetir();
  }, [verileriGetir]);

  useEffect(() => {
    if (seciliTarih) {
      const tarihStr = formatDateForAPI(seciliTarih);
      api.get(`/diyetisyen/program?tarih=${tarihStr}`)
        .then(res => setDiyetisyenProgrami(res.data))
        .catch(err => console.error("Program alınamadı", err));
    }
  }, [seciliTarih]);

  // Handler Fonksiyonları
  const handleTarihChange = (newDate) => {
    setSeciliTarih(newDate);
    setSeciliSaat(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!seciliDanisan || !seciliSaat) {
      setError("Lütfen bir danışan ve uygun bir saat seçimi yapın.");
      return;
    }
    
    const [saat, dakika] = seciliSaat.split(':');
    const randevuTarihi = new Date(seciliTarih);
    randevuTarihi.setHours(saat, dakika, 0, 0);

    const year = randevuTarihi.getFullYear();
    const month = String(randevuTarihi.getMonth() + 1).padStart(2, '0');
    const day = String(randevuTarihi.getDate()).padStart(2, '0');
    const localDateTimeString = `${year}-${month}-${day}T${saat}:${dakika}:00`;

    const formData = {
      tarih_saat: localDateTimeString,
      konu: konu
    };

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/diyetisyen/danisanlar/${seciliDanisan.value}/randevular`, formData);
      setSuccess('Randevu başarıyla oluşturuldu!');
      verileriGetir(); // Hem takvimi hem de listeyi yenile
      setSeciliDanisan(null);
      setKonu('');
      setSeciliSaat(null);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.mesaj || 'Randevu oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  };

  // Sıralanmış Randevu Listesi
  const siralanmisRandevular = useMemo(() => {
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);

    return [...randevuListesi]
      .filter(r => new Date(r.tarih_saat) >= bugun)
      .sort((a, b) => {
        const tarihA = new Date(a.tarih_saat);
        const tarihB = new Date(b.tarih_saat);
        return siralama === 'asc' ? tarihA - tarihB : tarihB - tarihA;
      });
  }, [randevuListesi, siralama]);

  // FullCalendar için etkinlikler
  const takvimEtkinlikleri = useMemo(() =>
    randevuListesi.map(r => ({
      id: String(r.id),
      title: r.danisan_ad_soyad + (r.konu ? ` · ${r.konu}` : ''),
      start: r.tarih_saat,
      backgroundColor: 'var(--accent-primary)',
      borderColor: 'var(--accent-primary)',
    })),
  [randevuListesi]);

  // Takvimde etkinliğe tıkla → detay sayfası
  const handleEventClick = (info) => {
    if (info.jsEvent) info.jsEvent.preventDefault();
    navigate(`/randevu/${info.event.id}`);
  };

  // Takvimde boş güne/saate tıkla → oluşturma formunu o tarih-saate ayarla
  const handleTakvimGunTikla = (info) => {
    const d = info.date;
    setSeciliTarih(d);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const slot = `${hh}:${mm}`;
    setSeciliSaat(zamanAraliklari.includes(slot) ? slot : null);
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // react-select için tema stilleri
  const selectStyles = {
    control: (styles) => ({ ...styles, backgroundColor: theme === 'dark' ? '#374151' : '#f8f9fa', borderColor: theme ==='dark' ? '#4b5563' : '#ced4da' }),
    menu: (styles) => ({ ...styles, backgroundColor: theme === 'dark' ? '#1f2937' : 'white' }),
    option: (styles, { isFocused }) => ({ ...styles, backgroundColor: isFocused ? (theme === 'dark' ? '#374151' : '#e9ecef') : 'transparent', color: theme === 'dark' ? 'white' : '#1f2937' }),
    singleValue: (styles) => ({ ...styles, color: theme === 'dark' ? 'white' : '#1f2937' }),
    input: (styles) => ({ ...styles, color: theme === 'dark' ? 'white' : '#1f2937' }),
    placeholder: (styles) => ({ ...styles, color: theme === 'dark' ? '#a1a1aa' : '#6c757d' }),
  };

  const handleSil = async (randevuId) => {
    if (!window.confirm("Bu randevuyu silmek istediğinizden emin misiniz?")) {
      return;
    }
    try {
      await api.delete(`/diyetisyen/randevular/${randevuId}`);
      // Silme sonrası listeyi ve takvimi anında güncellemek için verileri tekrar çek
      verileriGetir();
    } catch (err) {
      alert(err.response?.data?.mesaj || "Randevu silinirken bir hata oluştu.");
    }
  };


  if (loading) return <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'dark'} />;

  return (
    <>
      <Card ref={formRef} className="glass-card text-white mb-4">
        <Card.Header as="h2">Yeni Randevu Oluştur</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={5}>
                <h5 className="mb-3">1. Randevu Bilgileri</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Danışan Seçimi</Form.Label>
                  <Select options={danisanlar} styles={selectStyles} value={seciliDanisan} onChange={setSeciliDanisan} placeholder="Danışan ara veya seç..." isSearchable noOptionsMessage={() => "Danışan bulunamadı"}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Konu (İsteğe Bağlı)</Form.Label>
                  <Form.Control className="glass-input" type="text" value={konu} onChange={(e) => setKonu(e.target.value)} placeholder="Örn: Aylık Kontrol" />
                </Form.Group>
              </Col>
              <Col md={7}>
                <h5 className="mb-3">2. Tarih ve Saat Seçimi</h5>
                <div className="d-flex justify-content-center mb-3">
                  <Calendar 
                    onChange={handleTarihChange} 
                    value={seciliTarih} 
                    locale="tr-TR" 
                    minDetail="month"
                    minDate={new Date(new Date().getFullYear() - 1, new Date().getMonth(), 1)}
                    maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth() + 1, 0)}
                    next2Label={null}
                    prev2Label={null}
                  />
                </div>
                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  {zamanAraliklari.map(saat => (
                    <button
                      type="button"
                      key={saat}
                      className={`time-chip ${seciliSaat === saat ? 'selected' : ''}`}
                      disabled={diyetisyenProgrami.includes(saat)}
                      onClick={() => setSeciliSaat(saat)}
                    >
                      {saat}
                    </button>
                  ))}
                </div>
              </Col>
            </Row>
            <hr className="my-4" />
            {error && <Alert variant="danger" className="mt-3" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" className="mt-3">{success}</Alert>}
            <div className="d-flex justify-content-end">
              <Button type="submit" variant="primary" size="lg" disabled={loading}>
                {loading ? <Spinner as="span" size="sm" /> : 'Randevuyu Oluştur'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="glass-card text-white mb-4">
        <Card.Header as="h2">Randevu Takvimi</Card.Header>
        <Card.Body>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            buttonText={{ today: 'Bugün', month: 'Ay', week: 'Hafta', day: 'Gün' }}
            locale="tr"
            firstDay={1}
            allDaySlot={false}
            slotMinTime="09:00:00"
            slotMaxTime="18:00:00"
            nowIndicator={true}
            height="auto"
            events={takvimEtkinlikleri}
            eventClick={handleEventClick}
            dateClick={handleTakvimGunTikla}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            displayEventEnd={false}
          />
        </Card.Body>
      </Card>

      <Card className="glass-card text-white">
        <Card.Header as="h2" className="d-flex justify-content-between align-items-center">
          <span>Yaklaşan Randevular</span>
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={() => setSiralama(s => s === 'asc' ? 'desc' : 'asc')}
          >
            Sırala: {siralama === 'asc' ? 'En Yakın Tarih' : 'En Uzak Tarih'}
          </Button>
        </Card.Header>
        <ListGroup variant="flush">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {siralanmisRandevular.length > 0 ? (
            siralanmisRandevular.map(randevu => (
              <ListGroup.Item key={randevu.id} className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{new Date(randevu.tarih_saat).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                  <div className={theme === 'dark' ? 'text-white-50' : 'text-muted'}>
                    {randevu.danisan_ad_soyad}
                  </div>
                </div>
                {/* GÜNCELLENDİ: Butonlar bir grup içine alındı ve Sil butonu eklendi */}
                <ButtonGroup>
                  <Button as={Link} to={`/randevu/${randevu.id}`} size="sm" variant={`outline-${theme === 'dark' ? 'info' : 'primary'}`}>
                    Detay
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleSil(randevu.id)}>
                    Sil
                  </Button>
                </ButtonGroup>
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item>
              Yaklaşan bir randevu bulunmuyor.
            </ListGroup.Item>
          )}
        </ListGroup>
      </Card>
    </>
  );
}

export default RandevuTakvimi;